import type { MatchingPage, Page, WithAnyOfText } from "@nexxt/contracts";
import type {
  addressDto,
  ChangeUsersRolesInput,
  ChangeUsersRolesResult,
  ContactActionInput,
  CreateContactInput,
  CreateUserInput,
  contactDto,
  GetUserInput,
  ListAddressesInput,
  ListContactsInput,
  ListUsersInput,
  SetUserRolesInput,
  UpdateContactInput,
  UpdateUserInput,
  UserActionInput,
  userDto,
  userOptionsDto,
} from "@nexxt/contracts/party";
import type * as z from "zod";

// What the screens of an organization's users and directory ask of a gateway (ADR-173): one call per operation of
// party, with the input and the answer of its contract. Each app gives its own, on the client of its gateway, and the
// screens never see a route. The operations are those an organization's own administrator has
// (ExternalPartyService, ADR-172); what only Tomorrow calls the app writes on top of the screens.

export type User = z.output<typeof userDto>;
export type UserOptions = z.output<typeof userOptionsDto>;
export type Contact = z.output<typeof contactDto>;
export type Address = z.output<typeof addressDto>;

/** The input of an operation as the screens give it: what the contract coerces (`page`, `limit`, …) as numbers. */
type Given<I> = { [K in keyof I]: unknown extends I[K] ? number | undefined : I[K] };
type Read<I, R> = (input: Given<I>, signal?: AbortSignal) => Promise<R>;
type Write<I, R> = (input: I) => Promise<R>;

/** The operations of the users and directory screens, by the names of party's RPC methods. */
export interface PartyClient {
  /** With `anyOf`, as a query string carries it, the sets each user of the page is in (ADR-302): only Backstage asks. */
  listUsers: Read<WithAnyOfText<ListUsersInput>, MatchingPage<User>>;
  getUserOptions: Read<Record<string, never>, UserOptions>;
  getUser: Read<GetUserInput, User>;
  createUser: Write<CreateUserInput, User>;
  updateUser: Write<UpdateUserInput, User>;
  setUserRoles: Write<SetUserRolesInput, User>;
  changeUsersRoles: Write<ChangeUsersRolesInput, ChangeUsersRolesResult>;
  blockUser: Write<UserActionInput, User>;
  unblockUser: Write<UserActionInput, User>;
  disableUser: Write<UserActionInput, User>;
  enableUser: Write<UserActionInput, User>;
  listContacts: Read<ListContactsInput, Page<Contact>>;
  createContact: Write<CreateContactInput, Contact>;
  updateContact: Write<UpdateContactInput, Contact>;
  archiveContact: Write<ContactActionInput, Contact>;
  unarchiveContact: Write<ContactActionInput, Contact>;
  listAddresses: Read<ListAddressesInput, Page<Address>>;
}

let registered: PartyClient | undefined;

/** Once, when the app starts: before it, no users or directory screen loads (the route loaders read through it too). */
export function registerPartyClient(client: PartyClient): void {
  registered = client;
}

export function partyClient(): PartyClient {
  if (!registered) throw new Error("registerPartyClient was not called when the app started");
  return registered;
}
